import { useState, useRef, useEffect, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { usePartnerAuth } from '../../context/PartnerAuthContext';
import { usePartnerBooking } from '../../context/PartnerBookingContext';
import { supabase } from '../../lib/supabase';
import { formatCardDate, displayDeck, cruiseLabel } from '../../components/shared/helpers';
import ModalShell from '../../components/shared/ModalShell';

// Status → muted label shown on non-bookable (disabled) cards.
// (Duplicated from the customer Availability.js so that file stays untouched.)
const STATUS_LABEL = {
  closed: 'Online booking closed',
  sold_out: 'Sold out',
};

// Cruise card grid for one cruise group. Identical layout to the customer
// screen, except the price line shows the PARTNER's discounted rate (computed
// from `rate`, the partner's TAC %) with the public price struck through.
// Discount is DISPLAY-ONLY — the real charge is computed server-side (A3).
function CruiseGroupSection({ group, groupIndex, total, rate, onSelect }) {
  const isSingle = group.decks.length === 1;
  const scrollRef = useRef(null);
  const [canScrollMore, setCanScrollMore] = useState(!isSingle);

  // Track horizontal scroll position to hide the hint once fully scrolled right
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      setCanScrollMore(el.scrollWidth - el.clientWidth - el.scrollLeft > 4);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [isSingle]);

  return (
    <div>

      {/* Section label — prominent gold Cormorant, divider on each side */}
      <div className="flex items-center gap-4 md:gap-5 mb-5">
        <div className="flex-1 h-px bg-[rgba(212,168,67,0.2)]" />
        <div className="flex flex-col items-center shrink-0">
          <span className="font-cormorant text-[#C9A84C] text-2xl md:text-3xl leading-tight">
            {cruiseLabel(group, 'short')}
          </span>
          <span className="font-dm-sans text-base md:text-lg text-white/55 tracking-[0.06em] mt-0.5">
            {group.time}
          </span>
        </div>
        <div className="flex-1 h-px bg-[rgba(212,168,67,0.2)]" />
      </div>

      {/* Mobile scroll hint — gold, animated arrow, hidden once fully scrolled right */}
      {!isSingle && canScrollMore && (
        <p className="md:hidden flex items-center justify-end gap-1.5 font-dm-sans text-base font-medium text-[#C9A84C] mb-2 pr-0.5">
          Scroll to see more
          <span className="arrow-slide inline-block" aria-hidden="true">&rarr;</span>
        </p>
      )}

      {/* Mobile: horizontal snap scroll · Desktop: equal-height grid */}
      <div
        ref={scrollRef}
        className={`flex items-stretch gap-4 overflow-x-auto pb-3 snap-x snap-mandatory md:overflow-x-visible md:pb-0 md:snap-none ${
          isSingle
            ? 'md:justify-center'
            : 'md:grid md:grid-cols-2 md:gap-6 md:items-stretch'
        }`}
      >
        {group.decks.map((card) => {
          const disabled = !card.bookable;
          const hasDiscount = rate > 0;
          const discAdult = hasDiscount ? Math.round(card.priceAdult * (1 - rate / 100)) : card.priceAdult;
          const discChild = hasDiscount ? Math.round(card.priceChild * (1 - rate / 100)) : card.priceChild;
          return (
          <div
            key={card.key}
            className={`flex flex-col h-full w-[85vw] shrink-0 snap-start rounded-xl overflow-hidden border ${
              isSingle ? 'md:w-full md:max-w-[500px]' : 'md:w-auto'
            } ${disabled ? 'border-white/[0.05] bg-[#0f1c2d]' : 'border-white/[0.09] bg-[#162840]'}`}
          >
            {/* Photo — fixed height, never shrinks. Muted with an overlay when disabled. */}
            <div className="relative shrink-0">
              <img
                src={card.image}
                alt={card.imageAlt}
                className={`w-full h-[200px] object-cover rounded-t-xl ${disabled ? 'grayscale-[0.4]' : ''}`}
              />
              {disabled && (
                <div className="absolute inset-0 bg-[rgba(10,22,40,0.62)] rounded-t-xl" aria-hidden="true" />
              )}
            </div>

            {/* Content — fills remaining height, action pinned to bottom */}
            <div className="flex flex-col flex-1 p-4">
              <p className={`font-dm-sans text-base font-semibold ${disabled ? 'text-white/55' : 'text-white'}`}>
                {displayDeck(card.cruiseName, card.deckLabel)}
              </p>
              <p className={`font-dm-sans text-sm font-light mt-1 leading-relaxed ${disabled ? 'text-white/55' : 'text-white/60'}`}>
                {card.desc}
              </p>

              {/* Partner pricing — discounted rate shown prominently (display only) */}
              {hasDiscount ? (
                <div className="mt-3 flex flex-col gap-1">
                  <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
                    <span className={`font-dm-sans text-sm font-semibold ${disabled ? 'text-white/55' : 'text-[rgba(212,168,67,0.95)]'}`}>
                      &#8377;{discAdult.toLocaleString()} <span className="font-normal text-white/50">per adult</span>
                    </span>
                    <span className="font-dm-sans text-xs font-light text-white/35 line-through">
                      &#8377;{card.priceAdult.toLocaleString()}
                    </span>
                    <span className="font-dm-sans text-[10px] font-bold uppercase tracking-[0.06em] text-[#0A1628] bg-[#C9A84C] rounded-full px-1.5 py-[3px] leading-none">
                      Partner &minus;{rate}%
                    </span>
                  </div>
                  <div className="flex items-center flex-wrap gap-x-2">
                    <span className={`font-dm-sans text-sm font-medium ${disabled ? 'text-white/55' : 'text-[rgba(212,168,67,0.85)]'}`}>
                      &#8377;{discChild.toLocaleString()} <span className="font-normal text-white/50">per child</span>
                    </span>
                    <span className="font-dm-sans text-xs font-light text-white/35 line-through">
                      &#8377;{card.priceChild.toLocaleString()}
                    </span>
                  </div>
                </div>
              ) : (
                <p className={`font-dm-sans text-sm font-medium mt-3 ${disabled ? 'text-white/55' : 'text-[rgba(212,168,67,0.85)]'}`}>
                  &#8377;{card.priceAdult.toLocaleString()} per adult &middot; &#8377;{card.priceChild.toLocaleString()} per child
                </p>
              )}

              <div className="mt-auto pt-4">
                {disabled ? (
                  <div
                    className="w-full py-[11px] rounded-full border border-white/[0.12] bg-white/[0.02] text-center select-none"
                    aria-disabled="true"
                  >
                    <span className="font-dm-sans text-[13px] font-medium tracking-[0.06em] text-white/55">
                      {STATUS_LABEL[card.status] || STATUS_LABEL.sold_out}
                    </span>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => onSelect(card)}
                    className="w-full py-[11px] rounded-full bg-[#C9A84C] text-[#0A1628] font-dm-sans text-[13px] font-semibold tracking-[0.08em] active:scale-[0.97] hover:bg-[#B8973E] transition-[transform,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)]"
                  >
                    Select
                  </button>
                )}
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {/* Mobile-only hint that more cruise groups follow below */}
      {groupIndex < total - 1 && (
        <div className="md:hidden flex flex-col items-center gap-1.5 mt-6">
          <span className="font-dm-sans text-base font-semibold text-[#C9A84C] tracking-[0.04em] [text-shadow:0_0_14px_rgba(201,168,76,0.45)]">
            More cruises below
          </span>
          <svg className="chevron-bounce" width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
            <path d="M6 9L11 14L16 9" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      )}

    </div>
  );
}

// Static presentation assets (photos + deck blurb). Prices, times, and
// availability all come from the backend; only the imagery/copy is local.
// (Duplicated from the customer Availability.js so that file stays untouched.)
const DECK_PRESENTATION = {
  'Sunset Cruise|Upper Deck':   { image: '/cruises/sunset-upper-cover.webp',   alt: 'Guests watching the sun set over the Brahmaputra from the open-air upper deck',                desc: 'Open air · Bar & Restaurant' },
  'Sunset Cruise|Middle Deck':  { image: '/cruises/sunset-middle-cover.webp',  alt: 'Covered middle deck with paper lanterns and guests at riverside tables in golden-hour light', desc: 'Top covered, sides open · Live Singing & DJ · Bar & Restaurant' },
  'Sundown Cruise|Upper Deck':  { image: '/cruises/sundown-upper-cover.webp',  alt: 'Open-air upper deck lounge lit by planter lights under a starry night sky',                   desc: 'Open air · Bar & Restaurant' },
  'Sundown Cruise|Middle Deck': { image: '/cruises/sundown-middle-cover.webp', alt: 'Middle deck bar and cane seating glowing under paper lanterns at night',                      desc: 'Top covered, sides open · Live Singing & DJ · Bar & Restaurant' },
  'Dinner Cruise|Middle Deck':  { image: '/cruises/dinner-cover.webp',         alt: 'Bihu dancers in traditional Assamese attire performing on board',                             desc: 'Cultural & Folk Dance · Live Singing · Buffet' },
};

function presentationFor(cruiseName, deck) {
  return (
    DECK_PRESENTATION[`${cruiseName}|${deck}`] ||
    (deck === 'Upper Deck'
      ? { image: '/cruises/sunset-upper-cover.webp',  alt: 'Guests watching the sun set over the Brahmaputra from the open-air upper deck',                desc: deck }
      : { image: '/cruises/sunset-middle-cover.webp', alt: 'Covered middle deck with paper lanterns and guests at riverside tables in golden-hour light', desc: deck })
  );
}

// "17:00" / "17:00:00" → minutes since midnight, for chronological sorting.
// Unparseable times sort last (Infinity).
function toMinutes(t) {
  if (!t) return Infinity;
  const [hRaw, mRaw] = String(t).split(':');
  const h = parseInt(hRaw, 10);
  const m = parseInt(mRaw ?? '0', 10);
  if (Number.isNaN(h)) return Infinity;
  return h * 60 + (Number.isNaN(m) ? 0 : m);
}

// "17:00" / "17:00:00" → "5:00 PM"
function to12h(t) {
  if (!t) return '';
  const [hRaw, mRaw] = String(t).split(':');
  let h = parseInt(hRaw, 10);
  if (Number.isNaN(h)) return '';
  const m = (mRaw ?? '00').padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${ampm}`;
}

export default function PartnerAvailability() {
  const { partnerSession, partnerProfile, loadingAuth } = usePartnerAuth();
  const {
    selectedDate, passengers, availabilityCruises,
    partnerId, tacRate, holdId,
    setSelectedCruise, setPaxDetails, setAgreedTnC,
    setHoldId, setHoldExpiresAt, setAvailabilityCruises,
    holdExpiredNotice, setHoldExpiredNotice,
    fading, go, exitToDashboard,
  } = usePartnerBooking();


  // Partner discount rate (TAC %). Display only — never used for charging.
  const rate = Number(tacRate) > 0 ? Number(tacRate) : 0;

  // Hold-confirmation modal (the cruise being confirmed) + transient banner notice.
  const [modalCruise, setModalCruise] = useState(null);
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);

  // Fetch availability on entry (and whenever the date/party changes). The
  // availability function needs no partner-awareness — it returns public prices,
  // which we discount for display. Reused exactly from the customer flow.
  useEffect(() => {
    if (loadingAuth) return;
    if (!partnerSession || partnerProfile?.approval_status !== 'approved') return;
    if (selectedDate === null) return;

    let cancelled = false;
    const date = selectedDate;
    setLoading(true);
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('availability', {
          // partnerId asks for the partner window; the server checks approval.
          body: { date, partnerId, adults: passengers.adults, children: passengers.children, infants: passengers.infants },
        });
        if (cancelled) return;
        if (!error && data && data.cruises) setAvailabilityCruises(data.cruises);
      } catch (e) {
        // Keep any existing list if the fetch fails.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingAuth, partnerSession, partnerProfile, selectedDate, passengers.adults, passengers.children, passengers.infants]);

  // Group the flat availability list into cruise groups shaped for the card
  // layout. (Grouping/presentation logic reused verbatim from the customer flow.)
  const groups = useMemo(() => {
    const byCode = new Map();
    for (const c of availabilityCruises || []) {
      if (!byCode.has(c.cruiseCode)) {
        byCode.set(c.cruiseCode, {
          key: c.cruiseCode,
          // cruiseName is the stored name (the key every hold, pricing query and
          // event uses); the display names ride alongside for cruiseLabel() only.
          cruiseName: c.cruiseName,
          displayName: c.displayName ?? null,
          displayNameShort: c.displayNameShort ?? null,
          time: `${to12h(c.departureTime)} – ${to12h(c.endTime)}`,
          departureMinutes: toMinutes(c.departureTime), // shared by all decks; used for ordering
          decks: [],
        });
      }
      const pres = presentationFor(c.cruiseName, c.deck);
      byCode.get(c.cruiseCode).decks.push({
        key: `${c.cruiseCode}-${c.deck}`,
        deckLabel: c.deck,
        image: pres.image,
        imageAlt: pres.alt,
        desc: pres.desc,
        priceAdult: c.adultPrice ?? 0,
        priceChild: c.childPrice ?? 0,
        status: c.status,
        bookable: !!c.bookable,
        cruiseName: c.cruiseName,
        displayName: c.displayName ?? null,
        displayNameShort: c.displayNameShort ?? null,
        cruiseCode: c.cruiseCode,
        departureTime: c.departureTime,
        endTime: c.endTime,
        reportingTime: c.reportingTime,
      });
    }
    // Presentational order: bookable cruises first (a group is bookable if ANY
    // deck is bookable), then non-bookable; within each tier, ascending by the
    // cruise's departure time.
    return Array.from(byCode.values()).sort((a, b) => {
      const aBookable = a.decks.some((d) => d.bookable);
      const bBookable = b.decks.some((d) => d.bookable);
      if (aBookable !== bBookable) return aBookable ? -1 : 1;
      return a.departureMinutes - b.departureMinutes;
    });
  }, [availabilityCruises]);

  // Auto-dismiss the transient banner (sold-out / failed-switch) after a few
  // seconds. Expiry is a blocking modal, not a banner, so it is not handled here.
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(''), 6000);
    return () => clearTimeout(t);
  }, [notice]);

  // Re-fetch availability after a fresh sold-out, so the list reflects reality.
  const refreshAvailability = async () => {
    const date = selectedDate;
    try {
      const { data, error } = await supabase.functions.invoke('availability', {
        body: { date, partnerId, adults: passengers.adults, children: passengers.children, infants: passengers.infants },
      });
      if (!error && data && data.cruises) setAvailabilityCruises(data.cruises);
    } catch (e) {
      // Keep the existing list if the refresh fails.
    }
  };

  // Proceed from the modal → place a PARTNER hold via the edge function.
  const handleProceed = async () => {
    if (!modalCruise || creating) return;
    const cruise = modalCruise;
    const hadHold = !!holdId;          // distinguishes a switch from a fresh select
    const date = selectedDate;
    setCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke('hold', {
        body: {
          action: 'create',
          partnerId,                    // partner hold — NOT customerId
          cruiseType: cruise.cruiseName,
          deck: cruise.deckLabel,
          date,
          adults: passengers.adults,
          children: passengers.children,
          infants: passengers.infants,
        },
      });

      if (error || !data || data.error) {
        setModalCruise(null);
        // The server refuses dates outside the partner window (or in the past)
        // with a plain-language message; anything else is a generic failure.
        const dateRefused = data?.error === 'date_out_of_window' || data?.error === 'date_past';
        setNotice(dateRefused && data.message ? data.message : 'Something went wrong while reserving the table. Please try again.');
        return;
      }

      if (data.soldOut) {
        setModalCruise(null);
        if (hadHold) {
          // Switch attempt failed — the existing hold is untouched. Keep everything.
          setNotice('That cruise just sold out — your current hold is still active.');
        } else {
          setNotice('Sorry, that cruise just sold out');
          refreshAvailability();
        }
        return;
      }

      // Held — store the hold + selection, then continue to the cruise summary.
      setSelectedCruise(cruise);
      // The partner books for a GUEST, so every passenger name starts BLANK —
      // passenger 1 is NOT seeded with the partner's name.
      const rows = [];
      for (let i = 0; i < passengers.adults; i++) rows.push({ type: 'adult', label: `Adult ${i + 1}`, name: '', age: '', gender: '', food: '' });
      for (let i = 0; i < passengers.children; i++) rows.push({ type: 'child', label: `Child ${i + 1}`, name: '', age: '', gender: '', food: '' });
      for (let i = 0; i < passengers.infants; i++) rows.push({ type: 'infant', label: `Infant ${i + 1}`, name: '', age: '' });
      setPaxDetails(rows);
      setAgreedTnC(false);
      setHoldId(data.holdId);
      setHoldExpiresAt(data.expiresAt);
      setHoldExpiredNotice(false);
      setNotice('');
      setModalCruise(null);
      go('/partner/book/cruise');
    } catch (e) {
      setModalCruise(null);
      setNotice('Something went wrong while reserving the table. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  // Guards — wait for the auth check, then require an approved partner session,
  // and a selected date (otherwise there is nothing to fetch).
  if (loadingAuth) {
    return (
      <div className="w-full max-w-[480px] p-px rounded-2xl bg-gradient-to-b from-[rgba(212,168,67,0.32)] via-[rgba(255,255,255,0.06)] to-[rgba(255,255,255,0.02)] card-enter">
        <div className="bg-[#0D1E35] rounded-[15px] shadow-[0_40px_80px_rgba(0,0,0,0.55)] px-7 py-8">
          <p className="font-dm-sans text-sm text-white/55 font-light leading-relaxed text-center">
            Loading...
          </p>
        </div>
      </div>
    );
  }
  if (!partnerSession) return <Navigate to="/partner/login" replace />;
  if (partnerProfile?.approval_status !== 'approved') return <Navigate to="/partner" replace />;
  if (selectedDate === null) return <Navigate to="/partner/book/date" replace />;

  return (
    <div className={`w-full max-w-[1200px] mx-auto px-4 md:px-6 card-enter transition-opacity duration-[250ms] ${fading ? 'opacity-0' : 'opacity-100'}`}>

        {/* Header row: back arrow → date · Exit to Dashboard */}
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => go('/partner/book/date')}
            className="flex items-center justify-center w-8 h-8 rounded-full border border-[rgba(212,168,67,0.3)] hover:border-[rgba(212,168,67,0.65)] hover:bg-[rgba(212,168,67,0.06)] transition-[border-color,background-color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]"
            aria-label="Go back"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M9 2L4 7L9 12" stroke="#D4A843" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {/* No active hold on this step (a hold is placed on Proceed, which
              navigates onward) → exit immediately */}
          <button
            type="button"
            onClick={exitToDashboard}
            className="font-dm-sans text-[13px] font-medium text-[rgba(212,168,67,0.6)] hover:text-[#C9A84C] hover:underline underline-offset-2 transition-colors duration-200"
          >
            Exit to Dashboard
          </button>
        </div>

        <p className="font-dm-sans text-[12px] uppercase tracking-[0.18em] text-[rgba(201,168,76,0.85)] mb-2">
          Partner Booking
        </p>
        <h1 className="font-cormorant font-normal text-[2rem] md:text-[2.4rem] leading-[1.1] tracking-[-0.01em] text-white mb-4">
          Available Cruises
        </h1>

        {/* Summary bar — selected date and party, each editable */}
        <div className="border border-[rgba(255,255,255,0.12)] rounded-lg bg-[rgba(255,255,255,0.04)] px-4 py-3 mb-8 md:mb-10 flex flex-col gap-2.5">

          {/* Date row */}
          <div className="flex items-center gap-2.5">
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" className="shrink-0" aria-hidden="true">
              <rect x="2.5" y="3.5" width="11" height="10" rx="1.5" stroke="#C9A84C" strokeWidth="1.3" />
              <path d="M2.5 6.5h11" stroke="#C9A84C" strokeWidth="1.3" />
              <path d="M5.5 2v2.5M10.5 2v2.5" stroke="#C9A84C" strokeWidth="1.3" strokeLinecap="round" />
            </svg>
            <span className="flex-1 font-dm-sans text-sm font-light text-white">
              {formatCardDate(selectedDate)}
            </span>
            <button
              type="button"
              onClick={() => go('/partner/book/date')}
              className="font-dm-sans text-[13px] font-medium text-[#C9A84C] hover:underline underline-offset-2 transition-colors duration-200"
            >
              Edit
            </button>
          </div>

          {/* Divider */}
          <div className="h-px bg-white/[0.06]" />

          {/* Passenger row — editable, routes to the partner party step */}
          <div className="flex items-center gap-2.5">
            <svg width="16" height="16" viewBox="0 0 18 16" fill="none" className="shrink-0" aria-hidden="true">
              <circle cx="6.5" cy="5" r="2.5" stroke="#C9A84C" strokeWidth="1.3" />
              <path d="M2 13.5c0-2.6 2-4.2 4.5-4.2s4.5 1.6 4.5 4.2" stroke="#C9A84C" strokeWidth="1.3" strokeLinecap="round" />
              <path d="M12.4 4.2a2.3 2.3 0 0 1 0 4.4M13 9.6c1.9.4 3 1.9 3 3.9" stroke="#C9A84C" strokeWidth="1.3" strokeLinecap="round" />
            </svg>
            <span className="flex-1 font-dm-sans text-sm font-light text-white">
              {[
                `${passengers.adults} ${passengers.adults === 1 ? 'Adult' : 'Adults'}`,
                passengers.children > 0 ? `${passengers.children} ${passengers.children === 1 ? 'Child' : 'Children'}` : null,
                passengers.infants > 0 ? `${passengers.infants} ${passengers.infants === 1 ? 'Infant' : 'Infants'}` : null,
              ].filter(Boolean).join(' · ')}
            </span>
            <button
              type="button"
              onClick={() => go('/partner/book/passengers')}
              className="font-dm-sans text-[13px] font-medium text-[#C9A84C] hover:underline underline-offset-2 transition-colors duration-200"
            >
              Edit
            </button>
          </div>

        </div>

        {/* Transient banner — sold-out or a failed switch (non-blocking) */}
        {notice && (
          <div className="mb-6 rounded-lg border border-[rgba(212,168,67,0.4)] bg-[rgba(201,168,76,0.08)] px-4 py-3" role="status">
            <p className="font-dm-sans text-sm font-light text-white/70 leading-relaxed">
              {notice}
            </p>
          </div>
        )}

        {loading ? (
          <p className="font-dm-sans text-sm text-white/55 font-light text-center py-10 leading-relaxed">
            Loading cruises...
          </p>
        ) : groups.length === 0 ? (
          <p className="font-dm-sans text-sm text-white/55 font-light text-center py-10 leading-relaxed">
            No cruises available for this date
          </p>
        ) : (
          <div className="flex flex-col gap-10 md:gap-12">
            {groups.map((group, groupIndex) => (
              <CruiseGroupSection
                key={group.key}
                group={group}
                groupIndex={groupIndex}
                total={groups.length}
                rate={rate}
                onSelect={(card) => {
                  setNotice('');
                  // card.cruiseName is the stored name; the display names come with it.
                  setModalCruise({ ...card, time: group.time });
                }}
              />
            ))}
          </div>
        )}

        {/* Hold-confirmation modal */}
        {modalCruise && (
          <ModalShell
            ariaLabel="Reserve the table"
            onClose={creating ? undefined : () => setModalCruise(null)}
          >
            {/* Ornamental diamond rule */}
            <div className="flex items-center gap-3 mb-4 w-full max-w-[180px]">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent to-[rgba(212,168,67,0.4)]" />
              <div className="w-1.5 h-1.5 rotate-45 bg-[rgba(212,168,67,0.55)] shrink-0" />
              <div className="flex-1 h-px bg-gradient-to-l from-transparent to-[rgba(212,168,67,0.4)]" />
            </div>

            <h2 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-2">
              Reserve the table
            </h2>
            <p className="font-dm-sans text-sm font-medium text-[#C9A84C] mb-3">
              {/* No-break space before the dot: it wraps down with the deck, never ends a line */}
              {cruiseLabel(modalCruise, 'short')}&nbsp;&middot; {displayDeck(modalCruise.cruiseName, modalCruise.deckLabel)}
            </p>
            <p className="font-dm-sans text-sm font-light text-white/60 leading-relaxed mb-6">
              Once you proceed, the table is reserved while you complete the booking. If you do not finish in time, the table is released and offered to other guests.
            </p>

            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={handleProceed}
                disabled={creating}
                className={`w-full py-[14px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full transition-[transform,opacity,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35] ${
                  creating ? 'opacity-60 cursor-not-allowed' : 'active:scale-[0.97] hover:bg-[#B8973E] cursor-pointer'
                }`}
              >
                {creating ? 'Reserving...' : 'Proceed'}
              </button>
              <button
                type="button"
                onClick={() => { if (!creating) setModalCruise(null); }}
                disabled={creating}
                className="w-full py-[12px] rounded-full border border-white/[0.18] text-white/70 font-dm-sans text-[12.5px] font-medium tracking-[0.12em] uppercase hover:border-white/30 hover:text-white/90 transition-[border-color,color] duration-200 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </ModalShell>
        )}

        {/* Hold-expiry notice modal — blocks until acknowledged */}
        {holdExpiredNotice && (
          <ModalShell
            ariaLabel="Booking hold expired"
            onClose={() => setHoldExpiredNotice(false)}
          >
            {/* Ornamental diamond rule */}
            <div className="flex items-center gap-3 mb-4 w-full max-w-[180px]">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent to-[rgba(212,168,67,0.4)]" />
              <div className="w-1.5 h-1.5 rotate-45 bg-[rgba(212,168,67,0.55)] shrink-0" />
              <div className="flex-1 h-px bg-gradient-to-l from-transparent to-[rgba(212,168,67,0.4)]" />
            </div>

            <h2 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-3">
              Your hold has expired
            </h2>
            <p className="font-dm-sans text-sm font-light text-white/60 leading-relaxed mb-6">
              Your hold has ended and the table has been released. Select a cruise again to reserve a table and continue the booking.
            </p>

            <button
              type="button"
              onClick={() => setHoldExpiredNotice(false)}
              className="w-full py-[14px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full transition-[transform,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] hover:bg-[#B8973E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35]"
            >
              Choose a cruise
            </button>
          </ModalShell>
        )}

      </div>
  );
}
